import { yup } from "dyl-components";
import { applyVolumeBasedBulkPricingTierSchema } from "./volumeBasedSchema";

const getVolumeBasedRecurringPricingFrequencySchema = (frequency) => ({
    [`volume_based_recurring_${frequency}_volume_prices`]:
        yup.array().optional().when(
            ["model", "schedule", "volume_based_recurring_frequencies"],
            {
                is: (model, schedule, frequencies) => {
                    const active = frequencies?.includes(frequency);
                    return (
                        model === "volume" && schedule === "recurring" && active
                    );
                },
                then: (schema) => applyVolumeBasedBulkPricingTierSchema(schema),
                otherwise: (schema) => schema.nullable(true),
            }
        ),
    [`volume_based_recurring_${frequency}_max_discount_cycle`]: yup
        .number()
        .when(["model", "schedule", "volume_based_recurring_frequencies"], {
            is: (model, schedule, frequencies) => {
                const active = frequencies?.includes(frequency);
                return (
                    model === "volume" && schedule === "recurring" && active
                );
            },
            then: (schema) =>
                schema
                    .typeError("Max discount cycle must be a number")
                    .positive("Max discount cycle must be greater than 0")
                    .integer("Max discount cycle must be a whole number")
                    .max(100, "Max discount cycle cannot be more than 100")
                    .nullable(true),
            otherwise: (schema) => schema.nullable(true),
        }),
});

const volumeBasedRecurringProductSchema = {
    volume_based_recurring_frequencies: yup
        .array()
        .when(["model", "schedule"], {
            is: (model, schedule) =>
                model === "volume" && schedule === "recurring",
            then: (schema) =>
                schema
                    .of(yup.string())
                    .min(1, "At least one pricing frequency should be checked")
                    .required(
                        "At least one pricing frequency should be checked"
                    ),
            otherwise: (schema) => schema.nullable(true),
        }),
    ...getVolumeBasedRecurringPricingFrequencySchema("monthly"),
    ...getVolumeBasedRecurringPricingFrequencySchema("quarterly"),
    ...getVolumeBasedRecurringPricingFrequencySchema("semi-annually"),
    ...getVolumeBasedRecurringPricingFrequencySchema("annually"),
};

const paymentFrequencies = [
    "monthly",
    "quarterly",
    "semi-annually",
    "annually",
];

export function formatVolumeRecurringPriceData(price_data, maxDiscount) {
    const frequencies = price_data.volume_based_recurring_frequencies;
    const hasDiscount = maxDiscount && Number(maxDiscount) > 0;
    return {
        price: paymentFrequencies.reduce((data, frequency) => {
            const root = `volume_based_recurring_${frequency}`;
            const volumes = price_data[`${root}_volume_prices`];
            const isValid = applyVolumeBasedBulkPricingTierSchema(yup.array()).isValidSync(volumes);
            return {
                ...data,
                ...(isValid
                    ? {
                        [frequency]: {
                            active: frequencies?.includes(frequency),
                            volumes: volumes.map(
                                ({ end, max_billing_cycles_discount, life_time_discount, ...price }) => ({
                                    ...price,
                                    ...(end ? { end: Number(end) } : {}),
                                    ...(hasDiscount ? {
                                        max_billing_cycles_discount,
                                        life_time_discount: !!life_time_discount
                                    } : {})
                                })
                            )
                        },
                    }
                    : {}),
            };
        }, {}),
    };
}

export function transformVolumeRecurringPricingDetails(price_data) {
    return {
        volume_based_recurring_frequencies: paymentFrequencies.filter(
            (paymentFrequency) => price_data.price[paymentFrequency]?.active
        ),
        ...paymentFrequencies.reduce((data, frequency) => {
            return {
                ...data,
                [`volume_based_recurring_${frequency}_volume_prices`]:
                    price_data.price[frequency]?.volumes.map(v => ({
                        ...v,
                        max_billing_cycles_discount: v.max_billing_cycles_discount,
                        life_time_discount: v.life_time_discount
                    })) || [
                        {
                            start: 1,
                            end: null,
                            price: null,
                        },
                    ],
            };
        }, {}),
    };
}

export default volumeBasedRecurringProductSchema;
